import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { setSentryUser } from "@/sentry";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      setSentryUser(data);
      return data;
    } catch (err) {
      // 401 on first load is normal (no session). Other errors are logged for debugging.
      if (err?.response?.status !== 401) {
        console.error("Auth refresh failed:", err);
      }
      setUser(false);
      setSentryUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.access_token) localStorage.setItem("karm_token", data.access_token);
    const me = await refresh();
    return me || data.user;
  }, [refresh]);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data?.access_token) localStorage.setItem("karm_token", data.access_token);
    const me = await refresh();
    return me || data.user;
  }, [refresh]);

  const registerCompany = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register-company", payload);
    if (data?.access_token) localStorage.setItem("karm_token", data.access_token);
    await refresh();
    return data;
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Logout endpoint failure shouldn't block local sign-out; just log.
      console.warn("Logout request failed (clearing local session anyway):", err?.message || err);
    }
    localStorage.removeItem("karm_token");
    setUser(false);
    setSentryUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, signup, registerCompany, logout, refresh }),
    [user, loading, login, signup, registerCompany, logout, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
